import React, { useEffect, useRef } from 'react';
import { StaticImage } from 'gatsby-plugin-image';
import styled from 'styled-components';
import { srConfig } from '@config';
import sr from '@utils/sr';
import { usePrefersReducedMotion } from '@hooks';

const StyledAboutSection = styled.section`
  max-width: 900px;

  .inner {
    display: grid;
    grid-template-columns: 3fr 2fr;
    grid-gap: 50px;

    @media (max-width: 768px) {
      display: block;
    }
  }
`;
const StyledText = styled.div`
  ul.skills-list {
    display: grid;
    grid-template-columns: repeat(2, minmax(140px, 200px));
    grid-gap: 0 10px;
    padding: 0;
    margin: 17px 0 0 0;
    overflow: hidden;
    list-style: none;

    li {
      position: relative;
      margin-bottom: 10px;
      padding-left: 20px;
      font-family: var(--font-mono);
      font-size: var(--fz-xs);

      &:before {
        content: '▹';
        position: absolute;
        left: 0;
        color: var(--green);
        font-size: var(--fz-sm);
        line-height: 12px;
      }
    }
  }
`;
const StyledPic = styled.div`
  position: relative;
  max-width: 300px;

  @media (max-width: 768px) {
    margin: 50px auto 0;
    width: 70%;
  }

  .wrapper {
    ${({ theme }) => theme.mixins.boxShadow};
    display: block;
    position: relative;
    width: 100%;
    border-radius: var(--border-radius);

    &:hover,
    &:focus {
      outline: 0;
      transform: translate(-4px, -4px);

      &:after {
        transform: translate(8px, 8px);
      }

      .img {
        filter: none;
        mix-blend-mode: normal;
      }
    }

    .img {
      position: relative;
      border-radius: var(--border-radius);
      mix-blend-mode: multiply;
      filter: grayscale(100%) contrast(1);
      transition: var(--transition);
    }

    &:before,
    &:after {
      content: '';
      display: block;
      position: absolute;
      width: 100%;
      height: 100%;
      border-radius: var(--border-radius);
      transition: var(--transition);
    }

    &:before {
      top: 0;
      left: 0;
      background-color: var(--green);
    }

    &:after {
      border: 2px solid var(--green);
      top: 14px;
      left: 14px;
      z-index: -1;
    }
  }
`;

const About = () => {
  const revealContainer = useRef(null);
  const prefersReducedMotion = usePrefersReducedMotion();

  useEffect(() => {
    if (prefersReducedMotion) {
      return;
    }

    sr.reveal(revealContainer.current, srConfig());
  }, []);

  const skills = ['Go', 'Rust', 'Firecracker MicroVMs', 'Python', 'Kubernetes'];

  return (
    <StyledAboutSection id="about" ref={revealContainer}>
      <h2 className="numbered-heading">About Me</h2>

      <div className="inner">
        <StyledText>
          <div>
            <p>
              Hey there! I'm Nishanth, a Master of Software Engineering student at Carnegie Mellon
              University. I've had an interest in Computer Science ever since I was a kid, hacking
              about on a desktop computer for the first time. Deep down, I've always known I'd pick
              Computer Science as my main field of work. It's something that comes to me naturally
              and something that I find myself doing in my free time as well. I read research
              papers, watch techincal talks and letures and tinker with different technologies
              through my projects.
            </p>

            <p>
              During my undergradudate studies at IIT Roorkee, where I had the privilege of working
              at{' '}
              <a href="https://sdslabs.co/" target="_blank" rel="noopener noreferrer">
                SDSLabs
              </a>
              , a student-run technical group where we build production-level software to learn and
              experiment with new concepts. We also helped propagate the culture of open source
              throughout our campus. As a developer at SDSLabs, I've worked on a wide range of
              projects ranging from topics such as Cloud Computing, Game Development and Operating
              Systems.
            </p>

            <p>
              I've also worked at multiple startups:{' '}
              <a href="https://hevodata.com" target="_blank" rel="noopener noreferrer">
                Hevo Data
              </a>
              ,{' '}
              <a href="https://mudrex.com" target="_blank" rel="noopener noreferrer">
                Mudrex (YC W19)
              </a>{' '}
              <a href="https://uniacco.com" target="_blank" rel="noopener noreferrer">
                UniAcco
              </a>
              ,{' '}
              <a href="https://shape.xyz/" target="_blank" rel="noopener noreferrer">
                Shape (YC S21)
              </a>{' '}
              and{' '}
              <a href="https://singlestore.com" target="_blank" rel="noopener noreferrer">
                Singlestore
              </a>{' '}
              where I worked on a variey of projects, all of which I have enjoyed thoroughly.
            </p>

            <p>
              My main focus right now is databases, distributed systems and deterministic simulation
              testing.
            </p>

            <p>Here are a few technologies I’ve been working with recently:</p>
          </div>

          <ul className="skills-list">
            {skills && skills.map((skill, i) => <li key={i}>{skill}</li>)}
          </ul>
        </StyledText>

        <StyledPic>
          <div className="wrapper">
            <StaticImage
              className="img"
              src="../../images/me.jpeg"
              width={500}
              quality={95}
              formats={['AUTO', 'WEBP', 'AVIF']}
              alt="Headshot"
            />
          </div>
        </StyledPic>
      </div>
    </StyledAboutSection>
  );
};

export default About;
